import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  PhoneCall,
  PhoneMissed,
  ArrowRight,
  Check,
  X,
  RefreshCcw,
  ExternalLink,
  Filter,
  Loader2,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Painel de divergências Linkus ↔ Zoho.
 * Mostra leads onde o estado Zoho não bate com o histórico real de
 * chamadas do PBX. Admin/Gestor pode aplicar a sugestão (propaga para
 * Zoho) ou dispensar.
 */
const STATUS_TABS = [
  { key: "pending", label: "Pendentes", tone: "amber" },
  { key: "applied", label: "Aplicadas", tone: "emerald" },
  { key: "dismissed", label: "Dispensadas", tone: "slate" },
  { key: "auto_resolved", label: "Resolvidas Auto.", tone: "slate" },
];

const toneClasses = {
  amber: "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]",
  emerald: "border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]",
  slate: "border-border bg-muted text-foreground",
};

export default function LinkusDiscrepancies() {
  const [activeTab, setActiveTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [dismissDlg, setDismissDlg] = useState(null); // {id, reason}
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/linkus/discrepancies?status=${activeTab}&limit=500`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const apply = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/linkus/discrepancies/${id}/apply`);
      toast.success(`Aplicado no Zoho: ${data.new_status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao aplicar.");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async () => {
    if (!dismissDlg?.id) return;
    setBusyId(dismissDlg.id);
    try {
      await api.post(`/linkus/discrepancies/${dismissDlg.id}/dismiss`, {
        reason: dismissDlg.reason || "",
      });
      toast.success("Discrepância dispensada.");
      setDismissDlg(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao dispensar.");
    } finally {
      setBusyId(null);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/linkus/sync-now");
      if (data.skipped) {
        toast.warning(`Sync ignorada: ${data.skipped}`);
      } else {
        toast.success(
          `Sync OK: ${data.cdrs_fetched} CDR(s), ${data.discrepancies_upserted} nova(s) divergência(s).`,
        );
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na sync.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="linkus-discrepancies-root">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <PhoneCall className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground">
              Divergências PBX ↔ Zoho
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Comparação entre chamadas realizadas no PBX Yeastar e o estado
              actual em Zoho CRM. Aprova ou dispensa cada sugestão.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={syncNow}
            disabled={syncing}
            data-testid="linkus-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border bg-card hover:bg-muted/40 text-sm font-medium disabled:opacity-40"
          >
            <RefreshCcw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "A sincronizar…" : "Sync agora"}
          </button>
          <Link
            to="/settings/linkus"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border bg-card hover:bg-muted/40 text-sm font-medium"
          >
            <Filter className="w-4 h-4" /> Configurar
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-border">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`linkus-tab-${t.key}`}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-border bg-muted/40 text-center py-12 text-muted-foreground text-sm"
          data-testid="linkus-empty"
        >
          {activeTab === "pending"
            ? "Sem divergências pendentes. O Zoho está alinhado com o PBX."
            : "Sem registos."}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <DiscrepancyRow
              key={d.id}
              disc={d}
              busy={busyId === d.id}
              onApply={() => apply(d.id)}
              onDismiss={() => setDismissDlg({ id: d.id, reason: "" })}
            />
          ))}
        </div>
      )}

      {/* Dismiss dialog */}
      {dismissDlg && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={(e) => e.target === e.currentTarget && setDismissDlg(null)}
        >
          <div className="bg-card rounded-lg shadow-xl border border-border max-w-md w-full p-5">
            <h3 className="text-base font-semibold text-foreground">
              Dispensar divergência
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Explica porquê é que esta divergência não deve ser aplicada.
            </p>
            <textarea
              rows={3}
              maxLength={400}
              value={dismissDlg.reason}
              onChange={(e) =>
                setDismissDlg({ ...dismissDlg, reason: e.target.value })
              }
              placeholder="Ex.: cliente confirmou por email, não pelo telefone."
              className="mt-3 block w-full rounded-md border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDismissDlg(null)}
                className="px-4 py-2 rounded-md border border-border text-sm font-medium bg-card hover:bg-muted/40"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={dismiss}
                disabled={busyId === dismissDlg.id}
                data-testid="linkus-dismiss-confirm-btn"
                className="px-4 py-2 rounded-md bg-slate-800 hover:bg-foreground text-background text-sm font-semibold disabled:opacity-40"
              >
                Dispensar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DiscrepancyRow({ disc, busy, onApply, onDismiss }) {
  const isPending = disc.status === "pending";
  const hasSuggestion = !!disc.suggested_zoho_status;
  return (
    <div
      className="bg-card rounded-lg border border-border p-4"
      data-testid={`linkus-discrepancy-${disc.lead_id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Link
              to={`/leads/${disc.lead_id}`}
              className="text-base font-semibold text-foreground hover:text-primary"
            >
              {disc.lead_name}
            </Link>
            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          </div>
          <div className="text-xs text-muted-foreground font-mono">{disc.lead_phone || "sem telefone"}</div>

          {/* Zoho status transition */}
          <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded border ${toneClasses.slate} font-medium`}>
              {disc.current_zoho_status}
            </span>
            {hasSuggestion && (
              <>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded border ${toneClasses.emerald} font-medium`}>
                  {disc.suggested_zoho_status}
                </span>
              </>
            )}
          </div>

          <div className="mt-3 text-sm text-foreground leading-relaxed">
            {disc.reason}
          </div>

          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <PhoneCall className="w-3.5 h-3.5 text-[hsl(var(--success))]" />
              {disc.n_calls_answered} atendidas
            </span>
            <span className="inline-flex items-center gap-1">
              <PhoneMissed className="w-3.5 h-3.5 text-rose-500" />
              {(disc.n_calls_total || 0) - (disc.n_calls_answered || 0)} não atendidas
            </span>
            {disc.last_call_at && (
              <span>Última chamada: {disc.last_call_at}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        {isPending && (
          <div className="flex items-center gap-2 shrink-0">
            {hasSuggestion && (
              <button
                type="button"
                onClick={onApply}
                disabled={busy}
                data-testid={`linkus-apply-${disc.lead_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-primary-foreground text-xs font-semibold disabled:opacity-40"
              >
                <Check className="w-3.5 h-3.5" /> Aplicar
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              data-testid={`linkus-dismiss-${disc.lead_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-muted/40 text-xs font-semibold text-foreground disabled:opacity-40"
            >
              <X className="w-3.5 h-3.5" /> Dispensar
            </button>
          </div>
        )}
      </div>

      {/* Applied / dismissed audit */}
      {!isPending && (
        <div className="mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground">
          {disc.status === "applied" && (
            <span>
              Aplicado em {new Date(disc.applied_at).toLocaleString("pt-PT")}
              {disc.applied_by_name && ` por ${disc.applied_by_name}`}
              {disc.applied_status && ` → ${disc.applied_status}`}
            </span>
          )}
          {disc.status === "dismissed" && (
            <span>
              Dispensada em {new Date(disc.dismissed_at).toLocaleString("pt-PT")}
              {disc.dismissed_by_name && ` por ${disc.dismissed_by_name}`}
              {disc.dismiss_reason && ` — ${disc.dismiss_reason}`}
            </span>
          )}
          {disc.status === "auto_resolved" && disc.auto_resolved_at && (
            <span>
              Resolvida automaticamente em {new Date(disc.auto_resolved_at).toLocaleString("pt-PT")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
